import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import { InputAdornment, Typography } from "@mui/material";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import {
  getOptionLabel,
  getOptionValue,
  gstOptions,
  loadOptions,
} from "common/enums";
import {
  CONSUMER,
  SINGLE_PHASE,
  THREE_PHASE,
  htLoadOptions,
  singlePhaseOptions,
  tentionOptions,
} from "common/constant";

import { onlyNumberKeys } from "utils";

import { usePremiseTypeQuery } from "apis/PremiseType";
import { useConsumersQuery } from "apis/User";
import { useApplicantTypeQuery } from "apis/ApplicantType";

const premiseNames = ["temporary"];

const DemandReductionForm = ({ formik, t }) => {
  const { roles } = useSelector((state) => state.Login);

  const { data: { data: { items: applicantType } = {} } = {} } =
    useApplicantTypeQuery();
  const [premiseIds, setPremiseIds] = useState([]);
  const [loadDetailOptions, setLoadDetailOptions] = useState([]);
  const [supplyVoltageOptions, setSupplyVoltageOptions] = useState([]);

  const {
    values: {
      id,
      consumerNumber,
      applicationTypeId,
      applicant: { applicantTypeId, isGSTRegistered },
      connectionLoad: {
        powerRequirementUnit,
        totalPowerRequirement,
        loadDetails,
        tention,
        supplyVoltage,
      },
      extraValues: { isPanNumber, isGstNumber },
      inProgressApplicationId,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
    },
    setFieldValue,
  } = formik;

  const { data: { data: consumersOptions } = {} } = useConsumersQuery({
    applicationType: applicationTypeId,
    utilityId: getOptionValue("utilityOptions", "Power"),
  });

  const { data: { data: premiseType } = {} } = usePremiseTypeQuery({
    utilityId: 1,
  });

  const threePhaseOptions = useMemo(
    () =>
      tention === tentionOptions[1].value
        ? [
            { value: 33, label: "33 kV" },
            { value: 11, label: "11 kV" },
            { value: 415, label: "415 V" },
          ]
        : [{ value: 415, label: "415 V" }],
    [tention]
  );
  const isIndividualApplicantType = useMemo(
    () =>
      applicantType?.find(
        (application) => application.name.toLowerCase() === "individual"
      ).id === applicantTypeId,
    [applicantType, applicantTypeId]
  );

  const isDisable = useMemo(
    () =>
      (inProgressApplicationId !== id && !isAllApplicationsProcessCompleted) ||
      payableBillAmount > 0,
    [
      inProgressApplicationId,
      id,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
    ]
  );
  useEffect(() => {
    const updatedPremiseIds = [];
    premiseType?.forEach((premise) => {
      if (premiseNames.includes(premise.name.toLowerCase())) {
        updatedPremiseIds.push(premise.id);
      }
    });

    setPremiseIds(updatedPremiseIds);
  }, [premiseType]);

  useEffect(() => {
    if (
      formik.values.connectionLoad.decreaseOfLoad &&
      formik.values.connectionLoad.powerRequirement
    ) {
      formik.setFieldValue(
        "connectionLoad.totalPowerRequirement",
        formik.values.connectionLoad.powerRequirement -
          formik.values.connectionLoad.decreaseOfLoad
      );
    } else {
      formik.setFieldValue(
        "connectionLoad.totalPowerRequirement",
        formik.values.connectionLoad.powerRequirement
      );
    }
  }, [formik.values.connectionLoad.decreaseOfLoad]);

  useEffect(() => {
    const options = tention
      ? tention === tentionOptions[0].value && totalPowerRequirement <= 6
        ? loadOptions
        : htLoadOptions
      : [];
    if (
      loadDetails &&
      options.some(
        (option) =>
          option.value !== loadDetails && !(totalPowerRequirement <= 6)
      )
    ) {
      setFieldValue("connectionLoad.loadDetails", "");
    }
    setLoadDetailOptions(options);
  }, [tention, totalPowerRequirement]);

  useEffect(() => {
    const options =
      loadDetails === SINGLE_PHASE
        ? singlePhaseOptions
        : loadDetails === THREE_PHASE
        ? threePhaseOptions
        : [];
    if (
      supplyVoltage &&
      !options.some((option) => option.value === supplyVoltage)
    ) {
      setFieldValue("connectionLoad.supplyVoltage", "");
    }
    setSupplyVoltageOptions(options);
  }, [loadDetails, tention]);

  return (
    <>
      <Row>
        {roles.includes(CONSUMER) && (
          <Col md={4} sm={6} className="mt-3">
            <SelectField
              label={t("Customer Number") + "*"}
              name="utilityConsumerId"
              formik={formik}
              options={consumersOptions || []}
              getOptionLabel={(option) => option.consumerNumber}
              getOptionValue={(option) => option.id}
            />
          </Col>
        )}

        <Typography className="mb-3" lineHeight="1" variant="h6">
          {t("Customer Details")}
        </Typography>

        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Customer Name")}
            name="consumerName"
            formik={formik}
            disabled
          />
        </Col>

        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Current Tariff Category")}
            name="tariffName"
            formik={formik}
            disabled
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Current Usage Purpose")}
            name="extraValues.premiseUsagePurpose"
            formik={formik}
            disabled
          />
        </Col>
        {!isPanNumber && (
          <Col md={4} sm={6}>
            <InputFieldText
              label={
                isIndividualApplicantType
                  ? t("PAN Number")
                  : t("PAN Number") + "*"
              }
              name="applicant.panNumber"
              formik={formik}
              disabled={isDisable}
              onChange={(e) =>
                setFieldValue(
                  "applicant.panNumber",
                  e.target.value.toUpperCase()
                )
              }
              placeholder={
                isIndividualApplicantType
                  ? t("PAN Number")
                  : t("PAN Number") + "*"
              }
            />
          </Col>
        )}
        {!isGstNumber && (
          <Col md={4} sm={6}>
            <SelectField
              label={t("GST") + "*"}
              name="applicant.isGSTRegistered"
              onChange={(_, { value }) => {
                setFieldValue("applicant.isGSTRegistered", value);
                if (!value) {
                  setFieldValue("applicant.gstNumber", "");
                }
              }}
              formik={formik}
              disabled={isDisable}
              options={gstOptions}
            />
          </Col>
        )}
        {!isGstNumber && isGSTRegistered && (
          <Col md={4} sm={6}>
            <InputFieldText
              label={t("GST Number") + "*"}
              name="applicant.gstNumber"
              formik={formik}
              onChange={(e) =>
                setFieldValue(
                  "applicant.gstNumber",
                  e.target.value.toUpperCase()
                )
              }
              placeholder="GST Number"
              disabled={isDisable}
            />
          </Col>
        )}

        <Typography className="mb-3" lineHeight="1" variant="h6">
          {t("Load Details")}
        </Typography>

        <Row>
          <Col md={4}>
            <InputFieldText
              label={t("Existing Contract Demand")}
              name="connectionLoad.powerRequirement"
              formik={formik}
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {getOptionLabel("unitOptions", powerRequirementUnit)}
                  </InputAdornment>
                ),
                inputProps: { min: 0, onKeyDown: onlyNumberKeys },
              }}
              placeholder="Enter Contract Load"
              disabled
            />
          </Col>

          <Col md={4}>
            <SelectField
              label={t("Power Requirements") + "*"}
              name="connectionLoad.tention"
              formik={formik}
              options={tentionOptions}
              placeholder="Power Requirements"
              disabled={isDisable}
            />
          </Col>

          <Col md={4}>
            <SelectField
              label={t("Load Details") + "*"}
              name="connectionLoad.loadDetails"
              formik={formik}
              options={loadDetailOptions}
              getOptionLabel={(option) => option.label}
              disabled={isDisable}
            />
          </Col>

          <Col md={4}>
            <SelectField
              label={t("Supply Voltage") + "*"}
              name="connectionLoad.supplyVoltage"
              formik={formik}
              options={supplyVoltageOptions}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              placeholder="Select Supply Voltage"
              disabled={isDisable}
            />
          </Col>
          <Col md={4}>
            <InputFieldText
              label={t("Decrease contract demand") + "*"}
              name="connectionLoad.decreaseOfLoad"
              formik={formik}
              disabled={isDisable}
              type="number"
              InputProps={{
                inputProps: { min: 0, onKeyDown: onlyNumberKeys },
              }}
              placeholder={t("Enter contract demand")}
            />
          </Col>
          <Col md={4}>
            <InputFieldText
              label={t("Total Power Requirement") + "*"}
              name="connectionLoad.totalPowerRequirement"
              formik={formik}
              disabled
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {getOptionLabel(
                      "unitOptions",
                      tention === tentionOptions[0].value &&
                        premiseIds.includes(
                          formik.values.extraValues.premiseTypeId
                        )
                        ? totalPowerRequirement <= 125
                          ? 3
                          : 2
                        : totalPowerRequirement < 151
                        ? 1
                        : 2
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Col>
        </Row>
      </Row>
      {!!inProgressApplicationId &&
      inProgressApplicationId !== id &&
      !isAllApplicationsProcessCompleted &&
      !!consumerNumber ? (
        <Typography variant="body2" color="error">
          There are some incomplete applications associated with this consumer
          number. You are unable to fill the form.
        </Typography>
      ) : payableBillAmount > 0 ? (
        <Typography variant="body2" color="error">
          You have an unpaid amount of {payableBillAmount} RS. Please pay first
          to continue.
        </Typography>
      ) : null}
    </>
  );
};

DemandReductionForm.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(DemandReductionForm);
